import { UserType } from "@inrev/common";
import { GlobalSearchModal } from "@portal/components/ui/GlobalSearch/GlobalSearchModal";
import { KeyboardShortcut } from "@portal/components/ui/KeyboardShortcut";
import { usePlatform } from "@portal/hooks/usePlatform";
import { createContext, FC, useCallback, useContext, useMemo, useRef, useState } from "react";
import { type SearchOpenedVia, trackSearchOpened } from "../analytics";
import { useAuthenticatedUser } from "./AuthenticatedUserProvider";

export type GlobalSearchProviderProps = {
	children: React.ReactNode;
};

export type GlobalSearchContext = {
	isGlobalSearchShown: boolean;
	isGlobalSearchDisabled: boolean;
};

export type GlobalSearchDispatchContext = {
	openGlobalSearch: (openedVia: SearchOpenedVia) => void;
	closeGlobalSearch: () => void;
	setIsGlobalSearchDisabled: (disable: boolean) => void;
};

const defaultGlobalSearchContext: GlobalSearchContext = {
	isGlobalSearchShown: false,
	isGlobalSearchDisabled: false,
};

const defaultGlobalSearchDispatchContext: GlobalSearchDispatchContext = {
	openGlobalSearch: () => {},
	closeGlobalSearch: () => {},
	setIsGlobalSearchDisabled: () => {},
};

const GlobalSearchContext = createContext<GlobalSearchContext>(defaultGlobalSearchContext);
const GlobalSearchDispatchContext = createContext<GlobalSearchDispatchContext>(
	defaultGlobalSearchDispatchContext,
);

export const GlobalSearchProvider: FC<GlobalSearchProviderProps> = ({ children }) => {
	const [isGlobalSearchShown, setIsGlobalSearchShown] = useState(false);
	const [isGlobalSearchDisabled, setIsGlobalSearchDisabled] = useState(false);
	const { user } = useAuthenticatedUser();

	const globalSearchContextValue = useMemo(
		() => ({ isGlobalSearchShown, isGlobalSearchDisabled }),
		[isGlobalSearchShown, isGlobalSearchDisabled],
	);

	// Mirrored so the dispatch value below can keep empty deps — every result
	// row consumes it, and rebuilding it on each open/close re-renders them all.
	const stateRef = useRef({ isGlobalSearchShown, isGlobalSearchDisabled, userType: user.type });
	stateRef.current = { isGlobalSearchShown, isGlobalSearchDisabled, userType: user.type };

	const globalSearchDispatchContextValue = useMemo(
		() => ({
			openGlobalSearch: (openedVia: SearchOpenedVia) => {
				const { isGlobalSearchShown, isGlobalSearchDisabled, userType } = stateRef.current;
				if (isGlobalSearchDisabled || isGlobalSearchShown) {
					return;
				}
				trackSearchOpened(userType, openedVia);
				setIsGlobalSearchShown(true);
			},
			closeGlobalSearch: () => {
				setIsGlobalSearchShown(false);
			},
			setIsGlobalSearchDisabled: (disable: boolean) => {
				if (stateRef.current.isGlobalSearchShown && disable) {
					setIsGlobalSearchShown(false);
				}
				setIsGlobalSearchDisabled(disable);
			},
		}),
		[],
	);

	return (
		<GlobalSearchContext.Provider value={globalSearchContextValue}>
			<GlobalSearchDispatchContext.Provider value={globalSearchDispatchContextValue}>
				{children}
				<GlobalSearchComponent />
			</GlobalSearchDispatchContext.Provider>
		</GlobalSearchContext.Provider>
	);
};

export const useGlobalSearch = () => useContext(GlobalSearchContext);
export const useGlobalSearchDispatch = () => useContext(GlobalSearchDispatchContext);

const GlobalSearchComponent = () => {
	const { user } = useAuthenticatedUser();
	const { isGlobalSearchShown, isGlobalSearchDisabled } = useGlobalSearch();
	const { openGlobalSearch, closeGlobalSearch } = useGlobalSearchDispatch();
	const { isMac } = usePlatform();

	const handleShortcutPress = useCallback(() => {
		if (isGlobalSearchShown) {
			closeGlobalSearch();
		} else {
			openGlobalSearch("hotkey");
		}
	}, [isGlobalSearchShown, openGlobalSearch, closeGlobalSearch]);

	const isHotkeyEnabled = user.type === UserType.admin || user.type === UserType.agent;

	return (
		<>
			{!isGlobalSearchDisabled && isGlobalSearchShown && <GlobalSearchModal />}
			{isHotkeyEnabled && !isGlobalSearchDisabled && (
				<KeyboardShortcut
					shortcut={{ key: "k", modifiers: { meta: isMac, ctrl: !isMac } }}
					onShortcutPress={handleShortcutPress}
				/>
			)}
		</>
	);
};
